import { MIN_PRICES } from '@config/storageKeys';
import useStorage from '@hooks/useStorage';
import { BaseProperty } from '@interfaces/expedia/property';
import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useEffect, useState } from 'react';

interface UseMinimumPriceProps {
  hotels?: BaseProperty[];
  tickets?: TicketGroup[] | { ticket_groups: TicketGroup[] };
  ticketsCount?: number;
  selectedHotelPrice?: number | null;
  selectedTicketPrice?: number | null;
}

const useMinimumPrice = ({
  hotels = [],
  tickets = [],
  ticketsCount = 1,
  selectedHotelPrice = null,
  selectedTicketPrice = null,
}: UseMinimumPriceProps) => {
  const storage = useStorage();
  const initialMinPrices = JSON.parse(storage.getItem(MIN_PRICES) ?? '{}');
  const [ticketPrice, setTicketPrice] = useState(initialMinPrices?.ticket ?? 0);
  const [hotelPrice, setHotelPrice] = useState(initialMinPrices?.hotel ?? 0);
  const [priceLoading, setPriceLoading] = useState(false);

  // Handle hotel price
  useEffect(() => {
    // If a hotel is selected, use its price
    if (selectedHotelPrice !== null) {
      setHotelPrice(selectedHotelPrice);
      const minPrices = JSON.parse(storage.getItem(MIN_PRICES) ?? '{}');
      minPrices.hotel = selectedHotelPrice;
      storage.setItem(MIN_PRICES, JSON.stringify(minPrices));
      return;
    }

    // Otherwise find minimum from available hotels
    if (hotels?.length) {
      const mhp = Math.min(
        ...hotels.map((hot) => hot.price),
        Number.MAX_SAFE_INTEGER
      );

      const finalPrice = mhp === Number.MAX_SAFE_INTEGER ? 0 : mhp;

      setHotelPrice(finalPrice);
      const minPrices = JSON.parse(storage.getItem(MIN_PRICES) ?? '{}');
      minPrices.hotel = finalPrice;
      storage.setItem(MIN_PRICES, JSON.stringify(minPrices));
    }
  }, [hotels, selectedHotelPrice, storage]);

  // Handle ticket price
  useEffect(() => {
    // If a ticket is selected, use its price
    if (selectedTicketPrice !== null) {
      const finalPrice = selectedTicketPrice * ticketsCount;
      setTicketPrice(finalPrice);
      const minPrices = JSON.parse(storage.getItem(MIN_PRICES) ?? '{}');
      minPrices.ticket = finalPrice;
      storage.setItem(MIN_PRICES, JSON.stringify(minPrices));
      return;
    }

    const t = Array.isArray(tickets) ? tickets : tickets?.ticket_groups || [];

    if (t?.length) {
      setPriceLoading(true);
      const validTickets = t.filter((ticket) =>
        ticket.splits.includes(ticketsCount)
      );
      const min = validTickets.length
        ? Math.min(...validTickets.map((ticket) => ticket.retail_price))
        : 0;

      const finalPrice = min * ticketsCount;
      setTicketPrice(finalPrice);
      const minPrices = JSON.parse(storage.getItem(MIN_PRICES) ?? '{}');
      minPrices.ticket = finalPrice;
      storage.setItem(MIN_PRICES, JSON.stringify(minPrices));
      setPriceLoading(false);
    }
  }, [storage, tickets, ticketsCount, selectedTicketPrice]);

  return {
    ticketPrice,
    hotelPrice,
    priceLoading,
    setTicketPrice,
    setHotelPrice,
  };
};

export default useMinimumPrice;
